import { Injectable } from '@angular/core';
import { BrandingService, BrandingType } from 'shared/services/branding';
import { BcAnalytics } from '../analytics/analytics.service';
import { bcStates } from '../common/states';
import { NavigationEnd, Router } from '@angular/router';
import { WindowService } from 'shared/services/window/window.service';
import { ServiceWorkerService } from 'shared/services/service-worker/service-worker.service';
import { NotificationResult } from 'shared/models/notification/notification-result.model';

const Permission = {
  granted: 'granted',
  denied: 'denied'
};

/**
 * The paths to the available application icons for the desktop notification popup.
 */
const ICON_PATH = {
  BOLDCHAT: 'images/boldchat-logo_64x64_icon.png',
  BOLD360: 'images/bold_notification_logo.png'
};

@Injectable()
export class DesktopPopupService {

  private _iconPath: string = ICON_PATH.BOLD360;
  private _hasNotificationBlockedAlreadySent: boolean = false;
  private _isWorkViewActive: boolean = false;

  constructor(private _bcAnalytics: BcAnalytics,
              private _brandingService: BrandingService,
              private _router: Router,
              private _windowService: WindowService,
              private _serviceWorkerService: ServiceWorkerService) {
    this._brandingService.currentBranding.subscribe(this._onBrandingChanged.bind(this));
    this._router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd) {
        this._isWorkViewActive = ev.url === '/' + bcStates.layout.url;
      }
    });
  }

  /**
   * Subscription handler that is called when the user's preferred branding has been changed.
   * @param {BrandingType} newBranding - The new branding to be displayed.
   */
  private _onBrandingChanged(newBranding: BrandingType) {
    this._iconPath = newBranding === BrandingType.BoldChat
      ? ICON_PATH.BOLDCHAT
      : ICON_PATH.BOLD360;
  }

  private _filterMessage(message: string): string {
    return message.replace(/\r?\n/gm, '').replace(/\t/gm, '').replace(/ +/gm, ' ');
  }

  private _createNotification(message: string, extraOptions?: NotificationOptions): Promise<NotificationResult> {
    if (this._serviceWorkerService.supportsNotifications()) {
      return this._createServiceWorkerNotification(message, {
        icon: this._iconPath,
        ...extraOptions
      });
    } else {
      // Not including extraOptions, as some browsers refuse to display notifications if some options are not supported
      return this._createLegacyNotification(message, {
        icon: this._iconPath
      });
    }
  }

  private _createServiceWorkerNotification(message, options) {
    return this._serviceWorkerService.sendNotification(message, options);
  }

  private _createLegacyNotification(message: string, options: NotificationOptions): Promise<NotificationResult> {
    const win = this._windowService.getWindow();
    return new Promise(resolve => {
      let notification = new win.Notification(this._filterMessage(message), options);
      notification.addEventListener('click', () => {
        win.focus();
        notification.close();
        resolve({ type: 'click' });
      });
      notification.addEventListener('close', () => {
        resolve({ type: 'close' });
      });
    });
  }

  private _checkSupportAndPermission(): Promise<any> {
    const win = this._windowService.getWindow();
    if (!('Notification' in win)) {
      return Promise.reject('This browser does not support desktop notification');
    }
    if (win.Notification.permission === Permission.granted) {
      return Promise.resolve();
    }
    if (win.Notification.permission !== Permission.denied) {
      return win.Notification.requestPermission()
        .then(permission => {
          if (permission === Permission.granted) {
            return Promise.resolve();
          }
          return Promise.reject('Notification was denied');
        });
    }
    return Promise.reject('Notification was blocked');
  }

  public async sendNotification(message: string, extraOptions?: NotificationOptions): Promise<NotificationResult> {
    if (this._windowService.getWindow().document.hasFocus() && this._isWorkViewActive) {
      return Promise.resolve({ type: 'failed' });
    }
    try {
      await this._checkSupportAndPermission();
      return this._createNotification(message, extraOptions);
    } catch (err) {
      if (!this._hasNotificationBlockedAlreadySent) {
        this._bcAnalytics.eventTrack('NotificationBlocked', err);
        this._hasNotificationBlockedAlreadySent = true;
      }
      return { type: 'failed' };
    }
  }
}
